<template>
  <div id="app">
    <div id="self-menu" v-if="selfMenu" v-click-outside="selfMenuOutsideClick">
      <mdb-card>
        <mdb-card-header>
          <span style="float:right;"><a class="text-white" href="" @click.prevent="selfMenu=false">X</a></span>
        </mdb-card-header>
        <mdb-card-body class="text-left">
          <p v-if="availableLanguages.length > 1">
            <mdb-icon icon="language"/>
            <select id="language-select" v-model="selectLang">
              <option v-for="l in availableLanguages" :key="l.language" :value="l.value">{{ l.label }}</option>
            </select>
          </p>
          <p v-if="security.isTranslator()">
            <mdb-btn icon="language" v-if="translationMode"
                     @click="setTranslationMode(!translationMode)">
              {{ $t("common.menu.stopTranslating") }}
            </mdb-btn>
            <mdb-btn icon="language" v-if="!translationMode"
                     @click="setTranslationMode(!translationMode)">
              {{ $t("common.menu.startTranslating") }}
            </mdb-btn>

          </p>
          <p>
            <router-link header to="/notifications" @click.native="selfMenu=false">
              <mdb-icon icon="bell"/>
              {{ $t("common.menu.notifications") }}
              <mdb-badge v-if="notifications > 0" color="danger">{{ notifications }}</mdb-badge>
            </router-link>
            <a v-if="notifications > 0" href="" @click.prevent="clearNotifications">
              <mdb-badge color="warning">Clear</mdb-badge>
            </a>
          </p>
          <p>
            <router-link header to="/notifications?nav=settings" @click.native="selfMenu=false">
              <mdb-icon icon="cog"/>
              {{ capitalize($t("page.notifications.settings")) }}
            </router-link>
          </p>

          <p v-if="$route.path != '/user/profile'">
            <router-link header to="/user/profile" @click.native="selfMenu=false">
              <mdb-icon icon="user"/>
              {{ $t("common.menu.myProfile") }}
            </router-link>
          </p>
          <p
            v-if="settings.features.systemBilling && settings.billingEngine && settings.billingEngine != 'NoBillingEngine'">
            <router-link header to="/user/billing" @click.native="selfMenu=false">
              <mdb-icon icon="credit-card"/>
              {{ $t("common.menu.myAccount") }}
            </router-link>
          </p>
          <p>
            <a href="" @click.prevent="helpClick('popup')">
              <mdb-icon icon="question"/>
              {{ $t("common.menu.help") }}
            </a>
          </p>
          <p>
            <router-link header to="/logout" @click.native="selfMenu=false">
              <mdb-icon icon="sign-out-alt"/>
              {{ capitalize($t("common.button.logout")) }}
            </router-link>
          </p>
        </mdb-card-body>
      </mdb-card>
    </div>

    <div v-if="showNewRelease" id="new-release-available">
      <div>A newer version of the app is available.
        <mdb-btn icon="recycle" iconClass="ml-1" iconRight size="sm" @click="refreshApp">Refresh</mdb-btn>
        .
      </div>
      <div class="small">If the refresh doesn't work, you might have to clear your cache and reload again.</div>
    </div>

    <div v-if="userDetails && userDetails.proxied" id="proxy-info">
      You are proxied as {{ userDetails.name }}
      <mdb-btn icon="eye-slash" iconClass="ml-1" iconRight size="sm" @click.native="hideProxyNote">Temp Hide</mdb-btn>
      <mdb-btn icon="sign-out-alt" iconClass="ml-1" iconRight size="sm" @click.native="unproxy">UNDO</mdb-btn>
    </div>
    <div id="proxy-info" v-if="!userDetails.proxied && currentGroup && currentGroup.ownerProxied && isGroupPage">
      You are not a member of {{ currentGroup.name }}. Viewing as Owner.
      <mdb-btn icon="eye-slash" iconClass="ml-1" iconRight size="sm" @click.native="hideProxyNote">Temp Hide</mdb-btn>
    </div>
    <div v-if="envTag && !diagnosticMode" id="env-info" @click="hideEnv">
      <mdb-badge color="warning">{{ envTag }}</mdb-badge>
    </div>
    <div v-if="diagnosticMode" id="diag-info" @click="showDiagInfo=true">
      <mdb-badge color="warning">Diagnostics On</mdb-badge>
    </div>
    <mdb-progress v-if="executing" id="progress-bar" animated color="primary-color-dark" indeterminate striped
                  style="z-index:999"/>
    <div v-if="executing" id="executing-overlay"/>

    <header id="top-header">
      <mdb-navbar
        id="main-navbar"
        :scrollingOffset="20"
        :toggler="false"
        animation="3"
        color="primary-color"
        dark
        double
        expand="lg"
        position="top"
        scrolling
        style="z-index:1000;"
      >
        <div v-if="loggedIn" class="text-center icon-div main-menu-hamburger" style="z-index:9999">
          <a id='side-nav-link' href="" @click.prevent="toggleLeftSideNav">
            <mdb-icon id="open-side-nav" color="white" icon="bars" size="2x"/>
          </a>
        </div>
        <mdb-navbar-brand v-if="settings" waves @click.native="topLogoClick"><img
          :src="settings.topLogo" class="gp-top-logo">
        </mdb-navbar-brand>

        <mdb-navbar-nav right
                        v-if="settings && loggedIn && userDetails && userDetails.avatar">
          <div @click="toggleSelfMenu" style="cursor: pointer">
            <span class="navbar-avatar">
              <img :src="userDetails.avatar" alt="avatar image"
                   class="rounded-circle z-depth-0"
                   style="width:30px;"/>
              <span id="notification-bell" class="navbar-notification" v-if="notifications > 0">
                <mdb-icon icon="bell" color="danger"/>
              </span>
            </span>
            <span class="self-menu-name">{{ userDetails.name }}</span>
            <mdb-icon v-if="selfMenu" icon="chevron-up" style="font-size:.75rem;"/>
            <mdb-icon v-if="!selfMenu" icon="chevron-down" style="font-size:.75rem;"/>
          </div>
        </mdb-navbar-nav>

        <mdb-navbar-nav v-if="!loggedIn && settingsLoaded">
          <mdb-popover :forceShow="showLoginPopover" :options="{placement: 'top'}">
            <span v-show="showLoginPopover" slot="header">{{ $t("common.menu.loginOrRegister") }}</span>

            <mdb-dropdown slot="reference" class="nav-item" style="z-index:300000;" tag="li">
              <mdb-dropdown-toggle slot="toggle" navLink tag="a" waves-fixed>
                <mdb-icon icon="user"/>
              </mdb-dropdown-toggle>
              <mdb-dropdown-menu dropLeft>
                <mdb-dropdown-item>
                  <router-link to="/register" v-if="!settings.registerGoesToPublicLanding">
                    <mdb-icon icon="user-plus"/>
                    {{ $t("common.menu.register") }}
                  </router-link>
                  <router-link to="/" v-if="settings.registerGoesToPublicLanding">
                    <mdb-icon icon="user-plus"/>
                    {{ $t("common.menu.register") }}
                  </router-link>
                </mdb-dropdown-item>
                <mdb-dropdown-item>
                  <router-link to="/login">
                    <mdb-icon icon="key"/>
                    {{ $t("common.menu.login") }}
                  </router-link>
                </mdb-dropdown-item>
              </mdb-dropdown-menu>
            </mdb-dropdown>
          </mdb-popover>
        </mdb-navbar-nav>

        <mdb-navbar-nav right v-if="settings && !loggedIn && availableLanguages.length>1">
          <mdb-icon icon="language"/>
          <select id="language-select" v-model="selectLang">
            <option v-for="l in availableLanguages" :key="l.language" :value="l.value">{{ l.label }}</option>
          </select>
        </mdb-navbar-nav>

      </mdb-navbar>
    </header>
    <div>
      <mdb-side-nav v-if="loggedIn" :OpenedFromOutside.sync="showSideNav"
                    :logo="topLogo"
                    hidden
                    mask="strong" @update:OpenedFromOutside="toggledExternally"
      >
        <img v-if="loggedIn && !currentGroup" :src="userDetails.avatar" alt="avatar image"
             class="rounded-circle z-depth-0"
             style="width:50%;"/>
        <img v-if="loggedIn && currentGroup" :src="currentGroup.avatar" alt="avatar image"
             class="rounded-circle z-depth-0"
             style="width:50%;"/>

        <ul>
          <li class="link" v-if="settings && currentGroup && loggedIn && currentGroup.role && currentGroup.role != 'Public'">
            <p class="card-header">{{ currentGroup.name }}</p>
            <mdb-side-nav-nav v-if="currentGroup && loggedIn && currentGroup.role != 'Public'" class="text-left"
                              @toggleCat="catToggled">
              <mdb-side-nav-item :to="currentGroup.homeUrl" header icon="home" @click.native="showSideNav=false">
                {{ $t("common.menu.groupHome") }}
              </mdb-side-nav-item>
              <mdb-side-nav-item v-if="hasRole('Admin')"
                                 :to="currentGroup.homeUrl+'/broadcast'"
                                 icon="bullhorn"
                                 @click.native="navDone(null)">{{ $t("common.menu.broadcastMessage") }}
              </mdb-side-nav-item>

              <mdb-side-nav-item :to="currentGroup.homeUrl+'/forum'"
                                 v-if="settings.features.forum && currentGroup.forumProvider=='INTERNAL'"
                                 header icon="comments"
                                 @click.native="navDone(null)">{{ $t("common.menu.discussionForum") }}
              </mdb-side-nav-item>
              <mdb-side-nav-item :href="currentGroup.discourseSSO"
                                 v-if="settings.features.forum && currentGroup.forumProvider=='DISCOURSE'"
                                 header icon="comments">{{ $t("common.menu.discussionForum") }}
              </mdb-side-nav-item>

              <mdb-side-nav-cat icon="calendar" :name="capitalize($tc('common.label.event',2))"
                                v-if="settings && settings.features && settings.features.eventManagement">
                <mdb-side-nav-item :to="currentGroup.homeUrl+'/calendar'"
                                   icon="calendar"
                                   @click.native="navDone('Events')">{{ $t("common.menu.eventCalendar") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item :to="currentGroup.homeUrl+'/events'"
                                   icon="calendar-star"
                                   @click.native="navDone('Events')">{{ $t("common.menu.allEvents") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item :to="currentGroup.homeUrl+'/galleries'"
                                   v-if="settings.features.eventImages"
                                   icon="images"
                                   @click.native="navDone('Events')">{{ $t("common.menu.galleries") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Event Organizer')"
                                   :to="'/event/new/' + currentGroup.vanityPath"
                                   header icon="calendar-plus"
                                   @click.native="navDone('Events')">{{ $t("common.menu.newEvent") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Group Organizer')"
                                   :to="'/reservation/same-day/' + currentGroup.vanityPath"
                                   header icon="calendar-plus"
                                   @click.native="navDone('Events')">{{ $t("common.menu.sameDayReservations") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="currentGroup.allowSuggestions"
                                   :to="'/event/suggest/' + currentGroup.vanityPath"
                                   icon="lightbulb-on"
                                   @click.native="navDone('Events')">{{ $t("common.menu.suggestions") }}
                </mdb-side-nav-item>
              </mdb-side-nav-cat>
              <mdb-side-nav-item :to="currentGroup.homeUrl+'/members'"
                                 header icon="users"
                                 @click.native="navDone(null)">{{ $t("common.menu.members") }}
              </mdb-side-nav-item>

              <mdb-side-nav-item
                :to="'/member/' + currentGroup.memberId+'/id-card'"
                v-if="settings.features.idCard && currentGroup.enableIdCard"
                icon="id-card"
                @click.native="navDone('GroupMore')">{{ $t("common.menu.myIdCard") }}
              </mdb-side-nav-item>

              <mdb-side-nav-cat icon="angle-double-right" :name="$t('common.menu.more')">
                <mdb-side-nav-item
                  v-if="settings.features.groupBilling && (hasRole('Accounting') || currentGroup.shareFinances)"
                  :to="currentGroup.homeUrl+'/accounting'"
                  icon="money-check-edit"
                  @click.native="navDone('GroupMore')">{{ $t("common.menu.finances") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="currentGroup.campfireActive && settings.features.campfire"
                                   :to="currentGroup.homeUrl+'/campfire'"
                                   icon="fire"
                                   @click.native="navDone('GroupMore')">{{ $t("common.menu.campfireGames") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item :to="currentGroup.homeUrl+'/donate'"
                                   v-if="currentGroup.donationsEnabled"
                                   header icon="money-bill-wave"
                                   @click.native="navDone('GroupMore')">{{ $t("common.menu.donate") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item v-if="currentGroup.fileStorageEnabled && settings.features.fileStorage"
                                   :to="currentGroup.homeUrl+'/files'"
                                   icon="folder"
                                   @click.native="navDone('GroupMore')">{{ $t("common.menu.fileStorage") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item :to="currentGroup.homeUrl+'/leave'"
                                   icon="door-closed"
                                   @click.native="navDone('GroupMore')">{{ $t("common.menu.leaveGroup") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item :to="currentGroup.homeUrl+'/billing'"
                                   v-if="settings.features.groupBilling"
                                   header icon="money-bill-wave"
                                   @click.native="navDone('GroupMore')">{{ $t("common.menu.myBilling") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item :to="'/group/' + currentGroup.vanityPath + '/answers'"
                                   header
                                   icon="question" @click.native="navDone('GroupMore')">
                  {{ $t("common.menu.myGroupAnswers") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item v-if="currentGroup.presenterActive && settings.features.presenter"
                                   :to="currentGroup.homeUrl+'/presentation'"
                                   icon="presentation"
                                   @click.native="navDone('GroupMore')">{{ $t("common.menu.sumatoPresentations") }}
                </mdb-side-nav-item>
              </mdb-side-nav-cat>

              <mdb-side-nav-cat v-if="hasRole('Event Organizer')" icon="wrench" :name="$t('common.menu.groupSettings')">
                <mdb-side-nav-item v-if="hasRole('Admin')"
                                   :to="'/group/setup/' + currentGroup.vanityPath"
                                   icon="check"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.guidedSetup") }}
                </mdb-side-nav-item>


                <mdb-side-nav-item v-if="hasRole('Admin')"
                                   :to="currentGroup.homeUrl+'/basics'"
                                   icon="cogs"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.basics") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item v-if="hasRole('Banking')"
                                   :to="currentGroup.homeUrl+'/banking'"
                                   icon="piggy-bank"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.banking") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Remove Members')"
                                   :to="currentGroup.homeUrl+'/banned'"
                                   icon="ban"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.bannedUsers") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Edit Documents')"
                                   :to="currentGroup.homeUrl+'/card-pages'"
                                   icon="columns"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.cardPages") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Admin')"
                                   :to="'/custom-fields/member/' + currentGroup.vanityPath"
                                   icon="database"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.customFields") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Edit Documents')"
                                   :to="currentGroup.homeUrl+'/content'"
                                   icon="books"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.documentLibrary") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Edit Home Page')"
                                   :to="currentGroup.homeUrl+'/designer'"
                                   icon="pencil-ruler"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.homePageDesigner") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Event Organizer')"
                                   :to="currentGroup.homeUrl+'/image-library'"
                                   icon="images"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.imageLibrary") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Send Invites')"
                                   :to="currentGroup.homeUrl+'/invitations'"
                                   icon="inbox-out"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.inviteNewMembers") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Admin')"
                                   :to="currentGroup.homeUrl+'/reputation'"
                                   icon="ribbon"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.reputation") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Accounting') && currentGroup.billingRule != 'MEMBER_PAYS'"
                                   :to="currentGroup.homeUrl+'/subscription-plans'"
                                   icon="file-invoice-dollar"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.subscriptionPlans") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item v-if="hasRole('Event Organizer')"
                                   :to="currentGroup.homeUrl+'/venue'"
                                   icon="map-marker-edit"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.venues") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Group Organizer')"
                                   :to="currentGroup.homeUrl+'/assistant'"
                                   icon="tasks"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.virtualAssistant") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="hasRole('Admin')"
                                   :to="currentGroup.homeUrl+'/welcome-email'"
                                   icon="mailbox"
                                   @click.native="navDone('Settings')">{{ $t("common.menu.welcomeEmail") }}
                </mdb-side-nav-item>
              </mdb-side-nav-cat>
            </mdb-side-nav-nav>
          </li>
          <li>
            <div v-if="currentGroup" class="link">

              <p class="card-header">{{ settings.siteName }}</p>
            </div>
            <mdb-side-nav-nav v-if="loggedIn" class="text-left" @toggleCat="catToggled">
              <mdb-side-nav-item header
                                 icon="home" to="/" @click.native="navDone(null)">
                {{ $t("common.menu.platformHome") }}
              </mdb-side-nav-item>

              <mdb-side-nav-item header
                                 icon="globe" to="/explore" @click.native="navDone(null)">
                {{ $t("common.menu.exploreGroups") }}
              </mdb-side-nav-item>

              <mdb-side-nav-item v-if="settings.discourse && settings.discourse.enabled"
                                 icon="comments" :href="settings.discourse.endpoint">
                {{ $t("common.menu.communityForums") }}
              </mdb-side-nav-item>

              <mdb-side-nav-item
                header
                icon="bell" to="/notifications" @click.native="navDone(null)">
                {{ $t("common.menu.notifications") }}
              </mdb-side-nav-item>
              <mdb-side-nav-item
                header
                icon="envelope" to="/conversations" @click.native="navDone(null)">
                {{ $t("common.menu.privateMessaging") }}
              </mdb-side-nav-item>
              <mdb-side-nav-item header
                                 icon="user" to="/user/profile" @click.native="navDone(null)">
                {{ $t("common.menu.myProfile") }}
              </mdb-side-nav-item>
              <mdb-side-nav-item :to="'/calendar'"
                                 icon="calendar"
                                 v-if="settings.features.eventManagement"
                                 @click.native="navDone(null)">{{ $t("common.menu.myCalendar") }}
              </mdb-side-nav-item>
              <mdb-side-nav-item :to="'/user/events'"
                                 icon="list"
                                 v-if="settings.features.eventManagement"
                                 @click.native="navDone(null)">{{ $t("common.menu.myEvents") }}
              </mdb-side-nav-item>
              <mdb-side-nav-item header v-if="userDetails.mayCreateGroup"
                                 icon="plus-circle" to="/newGroup" @click.native="navDone(null)">
                {{ $t("common.menu.createGroup") }}
              </mdb-side-nav-item>
              <mdb-side-nav-cat icon="angle-double-right" :name="$t('common.menu.more')">
                <mdb-side-nav-item icon="key"
                                   to="/user/keys" @click.native="navDone('More')">
                  {{ $t("common.menu.apiKeys") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item icon="credit-card"
                                   v-if="settings.features.systemBilling && settings.billingEngine && settings.billingEngine != 'NoBillingEngine'"
                                   to="/user/billing" @click.native="navDone('More')">
                  {{ $t("common.menu.myAccount") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item icon="user-times"
                                   to="/user/blocks" @click.native="navDone('More')">
                  {{ $t("common.menu.blockedUsers") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="settings.features.issueTracker && settings.features.usersSeeIssues" icon="bug"
                                   to="/issues/report" @click.native="navDone('More')">{{ $t("common.menu.bugReport") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="settings.features.contactUs"
                                   icon="inbox-out"
                                   to="/contact" @click.native="navDone('More')">
                  {{ $t("common.menu.contactUs") }}
                </mdb-side-nav-item>

                <mdb-side-nav-item icon="question" to="/faq" @click.native="navDone('More')">FAQ</mdb-side-nav-item>
                <mdb-side-nav-item
                  v-if="settings.features.issueTracker  && ( security.hasRole('ISSUE_TRACKER') || settings.features.usersSeeIssues)"
                  icon="bomb"
                  to="/issues"
                  @click.native="navDone('More')">{{ $t("common.menu.issues") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="settings.features.jitsi" icon="handshake" to="/meeting"
                                   @click.native="navDone('More')">{{ $t("common.menu.onlineMeeting") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item icon="user-secret" to="/page/privacy" @click.native="navDone('More')">
                  {{ $t("common.label.privacyPolicy") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item icon="file-contract" to="/page/tos" @click.native="navDone('More')">
                  {{ $t("common.label.termsOfService") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="diagnosticMode" icon="wrench" to="#" @click.native="toggleDiagnostics">
                  Disable Diagnostics
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if=" (!diagnosticMode) && settings.enableDiagnostics" icon="wrench" to="#"
                                   @click.native="toggleDiagnostics">
                  Enable Diagnostics
                </mdb-side-nav-item>

                <mdb-side-nav-item icon="code-branch"
                                   to="#" @click.native="showVersion">
                  {{ $t("common.menu.version") }}
                </mdb-side-nav-item>

              </mdb-side-nav-cat>

              <mdb-side-nav-cat v-if="userDetails.isAdmin && settings.features.campfire"
                                icon="fire" :name="$t('common.menu.campfireAdmin')">
                <mdb-side-nav-item to="/campfire/card-set"
                                   @click.native="navDone('Campfire Admin')">{{ $t("common.menu.cardSets") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/campfire/game-deck"
                                   @click.native="navDone('Campfire Admin')">{{ $t("common.menu.gameDecks") }}
                </mdb-side-nav-item>
              </mdb-side-nav-cat>

              <mdb-side-nav-cat v-if="userDetails.isAdmin || userDetails.isTenantAdmin"
                                icon="wrench" :name="$t('common.menu.customization')"
              >
                <mdb-side-nav-item to="/admin/campaign" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.adCampaigns") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/card-pages"
                                   @click.native="navDone('Site Admin')">{{ $t("common.menu.cardPages") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/custom/forms" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.forms") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/custom/form-responses" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.formResponses") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/integrations" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.integrations") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/links" @click.native="navDone('Customization')">
                  {{ $t("common.menu.legacyLinks") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/demo-data" @click.native="navDone('Customization')">
                  Maintain Demo Data
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/custom/settings" @click.native="navDone('Customization')">
                  {{ $t("common.menu.settings") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/image" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.systemImage") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/pages" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.systemPages") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/custom-fields/user" @click.native="navDone('Customization')">
                  {{ $t("common.menu.userFields") }}
                </mdb-side-nav-item>

              </mdb-side-nav-cat>

              <mdb-side-nav-cat v-if="userDetails.isAdmin || userDetails.isTenantAdmin"
                                icon="key" :name="$t('common.menu.siteAdmin')">
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/users/1/proxy" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.adminProxy") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/announce" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.announcements") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/aws" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.awsServices") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/billing" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.billing") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/affiliate" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.brandsAffiliates") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/dashboard" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.adminDashboard") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/group" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.groupAdmin") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/group-redirector" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.groupRedirector") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/group-categories" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.groupTags") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="settings.features.issueTracker && !settings.features.usersSeeIssues"
                                   to="/issues"
                                   @click.native="navDone('Site Admin')">{{ $t("common.menu.issues") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/jobs" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.jobs") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/metrics" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.adminMetrics") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/query" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.query") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/rss" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.rssFeeds") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="settings.features.systemBilling" to="/user/subscription"
                                   @click.native="navDone('Site Admin')">{{ $t("common.menu.subscriptions") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="settings.features.systemBilling" to="/admin/subscription"
                                   @click.native="navDone('Site Admin')">{{ $t("common.menu.subscriptionPlans") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/stamp" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.stampRelease") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/storage-endpoints"
                                   @click.native="navDone('Site Admin')">{{ $t("common.menu.storageEndpoints") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/config" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.systemConfig") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="userDetails.isAdmin" to="/admin/tenants" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.tenants") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/admin/tenant-redirector" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.tenantRedirector") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/users" @click.native="navDone('Site Admin')">{{ $t("common.menu.userAdmin") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/custom-fields/user" @click.native="navDone('Site Admin')">
                  {{ $t("common.menu.userFields") }}
                </mdb-side-nav-item>
                <mdb-side-nav-item v-if="settings.features.systemVideos" to="/admin/videos"
                                   @click.native="navDone('Site Admin')">{{ $t("common.menu.videoAdmin") }}
                </mdb-side-nav-item>
              </mdb-side-nav-cat>

              <mdb-side-nav-cat v-if="security.isTranslator()"
                                icon="globe" name="Translations"
              >
                <mdb-side-nav-item to="/translate/buttons" icon="circle-dot" @click.native="navDone('Translations')">
                  Common Buttons
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/translate/daysMonths" icon="calendar-alt" @click.native="navDone('Translations')">
                  Day/Month Names
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/translate/labels" icon="tag" @click.native="navDone('Translations')">Common
                  Labels
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/translate/email-subjects" icon="envelope"
                                   @click.native="navDone('Translations')">Email Subjects
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/translate/menu-items" icon="bars" @click.native="navDone('Translations')">Menu
                  Items
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/translate/messaging" icon="comment" @click.native="navDone('Translations')">Messaging
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/new-user" icon="user-plus" @click.native="navDone('Translations')">New User Page
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/quick-connect" icon="bolt" @click.native="navDone('Translations')">Quick Connect
                  Page
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/register?localForm=Y" icon="id-card" @click.native="navDone('Translations')">Registration
                  Page
                </mdb-side-nav-item>
                <mdb-side-nav-item to="/translate/upload-download" icon="upload" @click.native="navDone('Translations')">Upload/Download
                </mdb-side-nav-item>

              </mdb-side-nav-cat>

              <mdb-side-nav-item id="logout-button" header href="#" icon="sign-out-alt" @click.native="logout">
                {{ capitalize($t("common.button.logout")) }}
              </mdb-side-nav-item>

            </mdb-side-nav-nav>
          </li>
        </ul>
      </mdb-side-nav>
    </div>
    <div v-if="showNameError" class="gp-has-header">
        <mdb-card class="m-5">
          <mdb-card-header class="warning-color text-white">
            <h3> <mdb-icon icon="warning"/>{{ capitalize($t('common.label.warning'))}}</h3>
          </mdb-card-header>
          <mdb-card-body class="text-left">
            <p>{{ $t("page.profile.changeNameWarning")}}</p>

            <mdb-input v-model="newName" id="newName" placeholder="Your New Profile Name Here">

            </mdb-input>

            <mdb-btn size="sm" rounded @click="changeProfileName" :disabled="!newName || newName.length < 4">{{$t("common.button.save")}}</mdb-btn>

          </mdb-card-body>
        </mdb-card>

    </div>
    <router-view :key="$route.fullPath"></router-view>

    <footer v-if="settings.poweredByLogo" class="text-center">
      <img class="powered-by-image" :src="settings.poweredByLogo"/>
    </footer>

    <mdb-modal v-if="showRequireTOS && $route.fullPath != '/page/tos'" size="lg">
      <mdb-modal-header :close="false" class="warning-color text-white">
        {{ capitalize($t("common.label.notice")) }}
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-card class="text-left">
          <mdb-card-body>
            {{ $t("common.label.tosRequired", {site: settings.siteName}) }}
            <router-link target="_blank" to="/page/tos">
              {{ $t("common.label.termsOfService") }}
              <mdb-icon icon="external-link"/>
            </router-link>
            .
          </mdb-card-body>
        </mdb-card>
        <mdb-btn rounded size="sm" @click="agreeTOS">{{ $t("common.button.iAgree") }}</mdb-btn>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal v-if="helpPopup" size="sm" @close="helpPopup=false">
      <mdb-modal-header :close="true" class="warning-color text-white">
        Help
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-card class="text-center">
          <mdb-card-body>
            <mdb-btn icon="question" @click="helpClick('faq')">{{ $t("common.label.readFaq") }}</mdb-btn>
            <mdb-btn icon="inbox-out" @click="helpClick('ask')">{{ $t("common.label.askForHelp") }}</mdb-btn>
          </mdb-card-body>
        </mdb-card>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal v-if="showDiagInfo && diagnosticMode" size="lg">
      <mdb-modal-header :close="false" class="warning-color text-white">
        About Diagnostics
      </mdb-modal-header>
      <mdb-modal-body>
        <p>{{ siteName }} will ask you to turn this on and provide further instructions if you've reported a problem
          and we need more information. Otherwise, you can turn this off.</p>
        <mdb-btn v-if="diagnosticMode" rounded size="sm" @click="toggleDiagnostics">Turn Off Now</mdb-btn>
        <mdb-btn v-if="diagnosticMode" rounded size="sm" @click="showDiagInfo=false">Leave On</mdb-btn>
      </mdb-modal-body>
    </mdb-modal>

    <mdb-modal v-if="releaseModal" size="lg" @close="releaseModal=false">
      <mdb-modal-header :close="true" class="warning-color text-white">
        {{ $t("common.label.versionInformation") }}
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-card>
          <mdb-card-body>
            <p><b>API (backend) release: </b>{{ release.backend }}</p>
            <p><b>Latest available UI (frontend) release: </b>{{ release.frontend }}</p>
            <p><b>Your UI version: </b>{{ release.userRunning }}</p>
          </mdb-card-body>
          <mdb-card-footer v-if="release.frontend != release.userRunning">
            {{ $t("common.label.refreshInstructions") }}
          </mdb-card-footer>
        </mdb-card>
        <mdb-btn icon="times-circle" rounded size="sm" @click="releaseModal=false">
          {{ capitalize($t("common.button.close")) }}
        </mdb-btn>
      </mdb-modal-body>
    </mdb-modal>
  </div>
</template>

<script>
import security from "./services/security";
import api from './services/groupsPlaceApi.js'
import {
  mdbAlert,
  mdbBadge,
  mdbBtn,
  mdbCard,
  mdbCardBody,
  mdbCardFooter,
  mdbCardHeader,
  mdbContainer,
  mdbDropdown,
  mdbDropdownItem,
  mdbDropdownMenu,
  mdbDropdownToggle,
  mdbFooter,
  mdbIcon,
  mdbInput,
  mdbModal,
  mdbModalBody,
  mdbModalHeader,
  mdbNavbar,
  mdbNavbarBrand,
  mdbNavbarNav,
  mdbNavbarToggler,
  mdbNavItem,
  mdbPopover,
  mdbProgress,
  mdbSideNav,
  mdbSideNavCat,
  mdbSideNavItem,
  mdbSideNavNav,
  mdbSpinner,
  mdbTooltip,
  waves
} from "mdbvue";
import Vue from "vue";
import gpCookie from "./services/gpCookie";

export default {
  name: 'App',
  components: {
    mdbNavbar,
    mdbNavItem,
    mdbNavbarNav,
    mdbNavbarToggler,
    mdbNavbarBrand,
    mdbFooter,
    mdbBtn,
    mdbModal,
    mdbCard,
    mdbCardHeader,
    mdbCardBody,
    mdbCardFooter,
    mdbContainer,
    mdbModalHeader,
    mdbModalBody,
    mdbDropdown,
    mdbDropdownMenu,
    mdbDropdownToggle,
    mdbDropdownItem,
    mdbTooltip,
    mdbIcon,
    mdbInput,
    mdbBadge,
    mdbAlert,
    mdbPopover,
    mdbSideNav,
    mdbSideNavNav,
    mdbSideNavCat,
    mdbSideNavItem,
    mdbProgress,
    mdbSpinner
  },
  computed: {
    isGroupPage() {
      if (this.$route.path.indexOf("/admin/group") >= 0) return false
      if (this.$route.path.indexOf("/group") >= 0) return true
      if (this.$route.path.indexOf("/event") >= 0) return true
      return false
    },
    security() {
      return security
    },
    apiKey() {
      return this.$store.state.apiKey
    },
    secret() {
      return this.$store.state.secret
    },

    translationMode() {
      return this.$store.state.translationMode
    },
    language() {
      return this.$store.state.language
    },
    userDetails() {
      return this.$store.state.userDetails
    },
    loggedIn() {
      return this.$store.state.loggedIn
    },
    notifications() {
      return this.$store.state.notifications
    },
    currentGroup() {
      return this.$store.state.currentGroup
    },
    executing() {
      return this.$store.state.executing
    },
    diagnosticMode() {
      return this.$store.state.diagnosticMode
    },
    settings() {
      return this.$store.state.settings
    },
    showRequireTOS() {
      if (this.settings && this.userDetails) {
        if (this.settings.requireTOS && !this.userDetails.tosAgreed && !this.executing) {
          return true
        }
      }
      return false
    },
    showNameError() {
      if (!this.loggedIn) return false
      if (!this.userDetails) return false
      if (!this.userDetails.email) return false
      let namePart = this.userDetails.email.split("@")[0]
      if (namePart == this.userDetails.name) return true
      return false
    },
  },
  watch: {
    userDetails(newVal) {
      if (newVal && this.settings) {
        this.newName = this.userDetails.suggestedName
        if (this.security.isTranslator()) {
          this.availableLanguages = []
          for (let l of this.settings.languages) {
            if (!l.translatingOnly || this.security.isTranslator()) {
              this.availableLanguages.push({value: l.language, label: l.nativeName})
            }
          }
        }
      }
    },
    notifications(newVal) {
      if (newVal > 0) {
        setTimeout(() => {
          this.startWiggle()
        }, 3000)
      } else {
        this.stopWiggle()
      }
    },
    selectLang(newVal, oldVal) {
      if (newVal && oldVal) {
        if (newVal == "undefined" || newVal == undefined) return
        console.log("Load language - selectLang", newVal)
        this.loadLanguage(newVal)
        this.$i18n.locale = newVal
        this.$store.dispatch("setLanguage", newVal)
        localStorage.setItem("lang", newVal)
        this.selfMenu = false
        if (this.loggedIn && this.userDetails.language != newVal) {
          api.post("/user/language/" + newVal, {}).then(
            (results) => {

            },
            (error) => {
              this.$notify.error({message: error.response.data, timeOut: 5000})
            }).then(() => {
            this.$store.dispatch("setExecuting", false)
          })
        }
      }
    }
  },
  async beforeMount() {
    let token = localStorage.getItem("apiKey")
    let secret = localStorage.getItem("secret")
    if (token && secret) {
      await this.$store.dispatch("setApiKey", token)
      await this.$store.dispatch("setSecret", secret)
      await this.$store.dispatch("setLoggedIn", true)
    }
    let diag = localStorage.getItem("diagnosticMode")
    if (diag) await this.$store.dispatch("toggleDiagnosticMode")
    await this.getUserDetails()
  },


  async mounted() {
    if (this.$route.query.login == 'Y') this.login()
    console.log("Loading default en")
    await this.loadLanguage("en")
    this.startNotifications()

    if (this.$route.query.camp) {
      console.log("Set campaign cookie ", this.$route.query.camp)
      gpCookie.setCookie("campaign", this.$route.query.camp, 5)
    }
    if (process.env.NODE_ENV != "production") {
      this.envTag = process.env.NODE_ENV.toUpperCase()
    }
    await this.loadSettings();
    setTimeout(() => {
      this.enableLoginPopover()
    }, 2000)

    if (navigator && navigator.serviceWorker) {
      console.log("Updating service workers.")
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let i = 0; i < registrations.length; i++) {
          let registration = registrations[i];
          console.log("Registration is ", registration.scope);
          registration.update();
        }
      });
    }

  },
  data() {
    return {
      selectLang: "en",
      showSideNav: false,
      envTag: null,
      avatarSnoozed: false,
      lastToggleId: null,
      release: "",
      releaseModal: false,
      showDiagInfo: false,
      topLogo: null,
      siteName: "Site",
      showLoginPopover: false,
      loadingSettings: false,
      selfMenu: false,
      helpPopup: false,
      settingsLoaded: false,
      availableLanguages: [],
      showNewRelease: false,
      loadedLanguages: [],
      selfTogglerTime: null,
      newName: ""
    }
  },
  mixins: [waves],
  directives : {
    'click-outside': {
      bind: function(el, binding, vNode) {
        // Provided expression must evaluate to a function.
        if (typeof binding.value !== 'function') {
          const compName = vNode.context.name
          let warn = `[Vue-click-outside:] provided expression '${binding.expression}' is not a function, but has to be`
          if (compName) { warn += `Found in component '${compName}'` }
          console.warn(warn)
        }
        // Define Handler and cache it on the element
        const bubble = binding.modifiers.bubble
        const handler = (e) => {
          if (bubble || (!el.contains(e.target) && el !== e.target)) {
            binding.value(e)
          }
        }
        el.__vueClickOutside__ = handler

        // add Event Listeners
        document.addEventListener('click', handler)
      },

      unbind: function(el, binding) {
        // Remove Event Listeners
        document.removeEventListener('click', el.__vueClickOutside__)
        el.__vueClickOutside__ = null

      }
    }
  },
  methods: {
    setStyle(src) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      console.log("Set stylesheet to ", src)
      link.href = src
      document.head.appendChild(link);
    },
    selfMenuOutsideClick(e) {
      if (!this.selfMenu) return
      if (!this.selfTogglerTime) return
      /* On iOS devices, this code fires right after opening the self menu, causing it to hide again immediately.
         Therefore, we save the time the toggler was opened, and if it's less than a second,
         we ignore the first outside click.
       */
      if (new Date() - this.selfTogglerTime < 1000) return
      if (this.selfMenu && e.target && e.target.className != "self-menu-name") this.selfMenu = false
    },
    refreshApp() {
      try {
        caches.open('v1').then(function (cache) {
          cache.delete('/index.html').then(function (response) {
            window.location.reload()
          });
        })
      } catch (e) {
        console.log("Error trying to clear cache and reload app.", e)
        this.showNewRelease = false
      }
    },
    toggleSelfMenu() {
      if (this.selfMenu) {
        this.selfMenu = false
        this.selfTogglerTime = null
        return
      }

      this.selfMenu = true
      this.selfTogglerTime = new Date()

    },
    loadLanguage(lang) {
      if (lang == "undefined" || lang == undefined) return
      if (this.loadedLanguages.includes(lang)) {
        console.log("Language " + lang + " already loaded.")
        return
      }
      this.loadedLanguages.push(lang)
      api.get("/public/i18n/" + lang).then(
        (results) => {
          this.$i18n.setLocaleMessage(lang, results.data)
        },
        (error) => {

        }).then(() => {

      })
    },
    setTranslationMode(bVal) {
      this.$store.dispatch("setTranslationMode", bVal)
      this.selfMenu = false
    },
    startWiggle() {
      Vue.nextTick().then(() => {
        let bell = document.getElementById("notification-bell")
        if (bell) {
          bell.classList.add("animated")
          bell.classList.add("wobble")
          bell.classList.add("infinite")
          setTimeout(() => {
            this.stopWiggle()
          }, 5000)
        }
      })
    },
    stopWiggle() {
      Vue.nextTick().then(() => {
        let bell = document.getElementById("notification-bell")
        if (bell) {
          bell.classList.remove("animated")
          bell.classList.remove("wobble")
          bell.classList.remove("infinite")
        }
      });
    },
    toggledExternally(v) {
      this.showSideNav = false
    },
    enableLoginPopover: function () {
      if (this.$route.fullPath != "/login" && this.$route.fullPath != "/register" && this.$route.path.indexOf("quick-connect") == -1) {
        this.showLoginPopover = true
        setTimeout(() => this.showLoginPopover = false, 3000)
      }
    },
    topLogoClick() {
      if (this.loggedIn) {
        if (this.$route.fullPath != "/") this.$router.push("/")
      } else {
        //window.location = process.env.PUBLIC_HOME
        window.location = this.settings.homeUrl
      }
    },
    agreeTOS() {
      this.$store.dispatch("setExecuting", false)
      api.post("/user/agreeTOS", {agreed: true}).then(
        (results) => {
          this.userDetails.tosAgreed = true
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    changeProfileName() {
      this.$store.dispatch("setExecuting", true)
      api.post("/user/updateName", {name: this.newName}).then(
        (results) => {
          this.userDetails.name = this.newName
        },
        (error) => {
          this.$notify.error({message: error.response.data, timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    toggleDiagnostics() {
      this.showSideNav = false
      this.showDiagInfo = false
      this.$store.dispatch("toggleDiagnosticMode")
    },
    loadSettings() {
      this.loadingSettings = true
      api.get("/public/settings/").then(
        (results) => {
          this.$store.dispatch("setSettings", results.data)
          this.settingsLoaded = true
          this.setStyle(results.data.stylesheet)
          this.setSkin((results.data.defaultSkin))
          if (results.data.maintenanceMode && !this.userDetails.isAdmin && this.$route.path != "/maintenance") {
            this.$router.push("/maintenance")
          }
          if (this.availableLanguages.length == 0) {
            for (let l of this.settings.languages) {
              if (!l.translatingOnly || this.security.isTranslator()) {
                this.availableLanguages.push({value: l.language, label: l.nativeName})
              }
            }
          }
          let savedLang = localStorage.getItem("lang");
          if (savedLang == "undefined" || savedLang == undefined) savedLang = null
          if (!savedLang) {
            let supportedLangs = results.data.languages
            let foundSupportedLang = false
            if (navigator.languages) {
              for (let l of navigator.languages) {
                for (let sl of supportedLangs) {
                  if (l == sl.language && !sl.translatingOnly && !foundSupportedLang) {
                    console.log("Loading supported lang ", l)
                    if (l != "en") this.loadLanguage(l)
                    this.selectLang = l
                    this.$i18n.locale = l
                    console.log("Loaded language " + l + " due to non-logged-in user's browser preference.")
                    localStorage.setItem("lang", l)
                    foundSupportedLang = true
                  }
                }
              }
            }
            if (!foundSupportedLang) {
              localStorage.setItem("lang", "en")
            }
          } else {
            if (savedLang != "en") {
              console.log("Loading saved lang ", savedLang)
              this.loadLanguage(savedLang)
            }
            this.$i18n.locale = savedLang
            this.selectLang = savedLang
            this.$store.dispatch("setLanguage", savedLang)
          }

          const link = document.createElement('link');
          link.rel = 'manifest';
          link.href = process.env.BACKEND_URL + "/index/manifest?tenantId=" + this.settings.tenantId
          const head = document.querySelector('head');
          head.appendChild(link);



        },
        (error) => {
          if (error.response.status == 400) this.$router.push("/invalidTenant")
          else this.$notify.error({message: "Could not connect to the system server at this time.", timeOut: 10000})
        }).then(() => {
        this.loadingSettings = false
        this.$store.dispatch("setExecuting", false)
      })
    },
    showVersion() {
      this.showSideNav = false
      this.$store.dispatch("setExecuting", false)
      api.get("/public/version/").then(
        (results) => {
          this.release = results.data
          this.release.userRunning = process.env.BUILD_VERSION, process.env.BACKEND_URL
          this.releaseModal = true
        },
        (error) => {
          this.$notify.error({message: "Error fetching page.", timeOut: 5000})
        }).then(() => {
        this.$store.dispatch("setExecuting", false)
      })
    },
    navToProfile() {
      this.avatarSnoozed = true
      this.$router.push('/user/profile')
    },
    navDone(category) {
      this.showSideNav = false
      //if (category) this.lastCat = category
    },
    catToggled(uid) {

    },
    unproxy: function () {
      api.get("/user/unProxy/", {}).then(
        (response) => {
          this.$store.dispatch("setUserDetails", response.data)
          window.location = "/admin/dashboard"
        },
        (error) => {
          this.$notify.error({
                               message: "Could not execute proxy switch. " + error.response.status,
                               timeOut: 5000,
                               position: 'top center'
                             })
        }
      );

    },
    toggleLeftSideNav() {
      if (!this.currentGroup && this.userDetails.hasOwnProperty("groups") && this.userDetails.groups.length > 0) {
        api.groupBasics(this.userDetails.groups[0].vanityPath, "member")
      }
      this.showSideNav = !this.showSideNav;
    },
    setSkin(skin) {
      document.body.className = ""
      document.body.classList.add(skin)
    },
    getUserDetails: async function () {
      if (!this.loggedIn) return
      await api.getUserDetails()
      if (this.userDetails.skin) {
        this.setSkin(this.userDetails.skin)
      }
      if (this.userDetails.language != this.language) {
        console.log("Loading language from user profile.", this.userDetails.langugage)
        this.loadLanguage(this.userDetails.language)
        this.$i18n.locale = this.userDetails.language
        await this.$store.dispatch("setLanguage", this.userDetails.language)
        console.log("Loaded language " + this.userDetails.language + " due to profile preference.")
        localStorage.setItem("lang", this.userDetails.language)
      }

    },
    hasRole: function (role) {
      if (!this.currentGroup) {
        return false
      }
      if (this.currentGroup.role == "Super") return true
      if (this.currentGroup.role == "Admin") return true

      if (role == "Admin") {
        if (this.currentGroup.role == "Admin") return true
        else return false
      }

      if (role == "Group Organizer") {
        if (this.currentGroup.role == "Group Organizer") return true
        else return false
      }

      if (role == "Event Organizer") {
        if (this.currentGroup.role == "Group Organizer" || this.currentGroup.role == "Event Organizer") return true
        else return false
      }
      if (!this.currentGroup.permissions) return false
      let hasRole = this.currentGroup.permissions.indexOf(role)
      if (hasRole > -1) return true
      else return false
    },
    helpClick: function (target) {
      if (target == "popup") {
        this.helpPopup = true
        this.selfMenu = false
      } else {
        this.helpPopup = false
      }
      if (target == "faq") this.$router.push("/faq")
      if (target == "ask") this.$router.push("/contact")
    },
    startNotifications: function () {
      this.getNotifications()
    },
    clearNotifications: function () {
      api.post("/notification/markAllUnread", {}).then(
        (results) => {
          this.$store.dispatch("setNotificationCount", 0)
          for (let i = 0; i < this.notifications.length; i++) {
            this.notifications[i].viewed = true
          }
        },
        (error) => {
        })
    },
    getNotifications: function () {
      if (!this.settings) return
      if (this.settings.maintenanceMode) return
      if (!this.loggedIn) {
        setTimeout(function () {
          this.getNotifications()
        }.bind(this), 10000)
        return
      }
      const n = api.get("/user/notifications", {}).then(
        (response) => {
          if (this.notifications > -1 && response.data.counts > this.notifications) {
            let msg
            if (response.data.counts == 1) msg = "You have an unread notification."
            else msg = "You have " + response.data.counts + " unread notifications."
            this.$notify.info({message: msg, timeOut: 5000, position: 'top center'})
          }
          this.$store.dispatch("setNotificationCount", response.data.counts)
          this.$store.dispatch("setToDos", response.data.todos)
          if (response.data.uiVersion > process.env.BUILD_VERSION) {
            console.log("Newer release is available. Local Version: " + process.env.BUILD_VERSION, process.env.BACKEND_URL + " Available: " + response.data.uiVersion)
            /*this.showNewRelease = true */
            //might not be ready for prime time.
          }
          setTimeout(function () {
            this.getNotifications()
          }.bind(this), 120000)
        },
        (error) => {
          console.log("Notification error... will try again later.")
          setTimeout(function () {
            this.getNotifications()
          }.bind(this), 120000)
        }
      )
    },
    hideProxyNote() {
      //Hide for screenshots, etc.
      let el = document.getElementById("proxy-info")
      let el2 = document.getElementById("env-info")
      if (el) el.hidden = true
      if (el2) el2.hidden = true
      setTimeout(function () {
        if (el) el.hidden = false;
        if (el2) el2.hidden = false
      }, 20000)
    },
    hideEnv() {
      //Hide for screenshots, etc.
      let el = document.getElementById("env-info")
      el.hidden = true
      setTimeout(function () {
        el.hidden = false;
      }, 20000)
    },
    logout: function () {
      this.toggleLeftSideNav()
      if (!this.loggedIn) return
      this.$router.push("/logout")
    },
  }
}
</script>

<style lang="scss">
$image-path: '~/mdbvue/lib/img'; // image path variable update
//@import '~mdbvue/lib/scss/mdb-pro.scss'; // main mdb scss pro file
//@import './src/assets/scss/mdb-pro';
@import url('https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap'); // font import
</style>

<style>
#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

@media screen and (max-width: 749px ) {
  .gp-has-header {
    margin-top: 75px;
  }

  .gp-top-logo {
    height: 25px;
    padding-left: 5px;
  }

  #progress-bar {
    position: fixed;
    top: 0;
    padding-top: 80px;
  }

}

@media screen and (min-width: 750px ) {
  .gp-has-header {
    margin-top: 100px;
  }

  .gp-top-logo {
    height: 40px;
    padding-left: 15px;
  }

  #progress-bar {
    position: fixed;
    top: 0;
    padding-top: 95px;
  }
}

.cursor-pointer {
  cursor: pointer;
}

#new-release-available {
  position: fixed;
  bottom: 0;
  padding: 10px;
  background-color: #fb3;
  color: white;
  z-index: 1000;
}


#proxy-info {
  position: fixed;
  bottom: 0;
  padding: 10px;
  background-color: #ff3547;
  color: white;
  z-index: 1000;
}

#env-info, #diag-info {
  position: fixed;
  top: 60px;
  left: 0;
  z-index: 1001;
}


#executing-overlay {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2;
  cursor: pointer;
}

.card-header-truncated {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.full-view {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  min-height: 100vh;
}

.gp-full-overlay {
  min-height: 100vh;
}


.gp-custom-select:focus {
  outline: none;

}

.gp-custom-select {
  width: 100%;
  height: calc(1.5em + .75rem + 2px);
  padding: .375rem 1.75rem .375rem .75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  vertical-align: middle;
  background: #fff url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' heig…0 4 5'%3e%3cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e") no-repeat right .75rem center/8px 10px;
  border-bottom: 1px solid #ced4da;
  border-top: none;
  border-right: none;
  border-left: none;
}

.editor-label {
  font-size: 1rem;
  font-weight: 300;
  color: #757575;
  cursor: text;
}

.number-input > input[type=number] {
  text-align: right;
}

.navbar-avatar {
  position: relative;
}

.powered-by-image {
  max-width: 100px;
}

.navbar-notification {
  position: absolute;
  top: -17px;
  left: 20px;
}

#self-menu {
  z-index: 5500;
  position: fixed;
  top: 55px;
  right: 0;
  min-width: 200px;
}

.main-menu-hamburger {
  margin-right: 15px;
}

@media screen and (max-width: 749px ) {
  .self-menu-name {
    display: none;
  }
}

</style>
